import Button from 'Components/Base/Button'
import Row from 'Components/Base/Row'
import Typography from 'Components/Base/Typography'
import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { setLoading } from 'reducers/tempSlice'
import styled, { useTheme } from 'styled-components'
import { IntakeMedication } from 'types/Medication'
import { useAppSelector } from '../../../hooks/hooks'
import usePatientIntake from '../../../hooks/usePatientIntake'
import { getAccessToken } from '../../../reducers/authSlice'
//import { ButtonOption } from '../components/OptionCard'
import Progress from '../components/Progress'
import MedSelectCard from './MedSelectCard'
import SearchDropdown from './SearchMed/SearchDropdown'
//import MedicationCard from './MedicationCard'

interface Props {
  backPressed: () => void
  onSubmit: () => void
}

export enum MedicationFrequency {
  Daily,
  Weekly,
  BiWeekly,
  Monthly,
  AsNeeded,
}
export type MedicationDetails = {
  formId?: number
  medicationName?: string
  strength?: string
  quantity?: number
  frequency?: MedicationFrequency
  reasonForTaking?: string
}

type Med = { tradeName: string; strength: string; gpi: string }

export default function Medications({ onSubmit, backPressed }: Props) {
  const [addingMedication, setAddingMedication] = useState(false)
  const [medicationsFromFillHistory, setMedicationsFromFillHistory] = useState<IntakeMedication[]>([])

  const dispatch = useDispatch()
  const accessToken = useAppSelector(getAccessToken)
  const { getMedicationInfo, postMedicationInfo } = usePatientIntake({ token: accessToken })
  const theme = useTheme()

  useEffect(() => {
    dispatch(setLoading(true))
    async function getPhysicianState() {
      const response = await getMedicationInfo()
      const data = await response.data

      if (data.medications.length !== 0 || data.medicationsFromFillHistory.length !== 0) {
        const meds = data.medications.map((med) => ({
          ...med,
          checked: true,
        }))

        const medsFromHistory = data.medicationsFromFillHistory.map((med) => ({
          ...med,
          checked: false,
        }))

        // Combine both arrays
        const combined = [...meds, ...medsFromHistory]
        setMedicationsFromFillHistory(combined)
        dispatch(setLoading(false))
      } else {
        dispatch(setLoading(false))
      }
    }
    getPhysicianState()
  }, [])

  const addSearchMed = (med: Med) => {
    setMedicationsFromFillHistory([
      ...medicationsFromFillHistory,
      { name: med.tradeName, strength: med.strength, gpi: med.gpi, checked: true, patientAdded: true },
    ])
  }
  const removeSearchMed = (med: IntakeMedication) => {
    const meds = medicationsFromFillHistory.filter((m) => med !== m)
    setMedicationsFromFillHistory(meds)
  }

  const onSelect = (med: IntakeMedication) => {
    setMedicationsFromFillHistory((medicationsFromFillHistory) =>
      medicationsFromFillHistory.map((item) => (item.name === med.name ? { ...item, checked: !item.checked } : item)),
    )
  }

  const handleSubmit = () => {
    postMedicationInfo(medicationsFromFillHistory.filter((m) => m.checked))

    onSubmit()
  }

  const handleBack = () => {
    postMedicationInfo(medicationsFromFillHistory.filter((m) => m.checked))
    backPressed()
  }

  return (
    <Container>
      <Card>
        <Progress current={2} total={3} />
        <Header>Your Medications</Header>
        <Para>
          Thanks to our connection with your health plan, we can show your medication history. Please go through the list and check what you still
          use.
        </Para>

        {medicationsFromFillHistory.length > 0 &&
          medicationsFromFillHistory.map((medication, i) => (
            <MedSelectCard
              medication={medication}
              handleSelect={() => onSelect(medication)}
              key={i}
              removeMedication={(med: IntakeMedication) => removeSearchMed(med)}
            />
          ))}
        {addingMedication && (
          <SearchDropdown
            onSelect={(med: Med) => {
              addSearchMed(med)
              setAddingMedication(!addingMedication)
            }}
            onClose={() => setAddingMedication(!addingMedication)}
          />
        )}
        {!addingMedication && (
          <Button
            text="Add"
            leftIcon="circle-plus"
            backgroundColor={theme.colors.status.good}
            styles={{ marginBottom: 30 }}
            onClick={() => setAddingMedication(!addingMedication)}
          />
        )}
        <Row styles={{ width: '100%', justifyContent: 'space-between' }}>
          <Button
            text="Back"
            styles={{ marginBottom: 30, background: 'none', color: theme.colors.primary1, border: `1px solid ${theme.colors.primary1}` }}
            width={100}
            onClick={() => handleBack()}
          />
          <Button text="Continue" styles={{ marginBottom: 30 }} width={150} onClick={() => handleSubmit()} />
        </Row>
      </Card>
      <ImgColumn>
        <Image src="https://triacentral.blob.core.windows.net/dev-storage/portal/enrollment/Did%20you%20Know%201.png" />
        <Typography size={13}>*Tria Health’s Book of Business Average</Typography>
      </ImgColumn>
    </Container>
  )
}

const Container = styled.div`
  font-family: Raleway;
  display: flex;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  align-items: center;
  justify-content: space-between;
  height: fit-content;
  @media only screen and (max-width: 600px) {
    margin-top: 0px;
  }
`

const Card = styled.div`
  font-family: Raleway;
  max-width: 600px;
  height: fit-content;
  min-height: 600px;
  padding: 40px;
  text-align: left;
  @media only screen and (max-width: 600px) {
    margin-top: 0px;
    padding: 0px 20px 40px;
  }
`

const Header = styled.h1`
  font-weight: 700;
  font-size: 36px;
  margin-bottom: 20px;
`

const Para = styled.p`
  font-size: 16px;
  margin-bottom: 20px;
`

const Image = styled.img`
  max-height: 530px;
`

const ImgColumn = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  @media only screen and (max-width: 1200px) {
    display: none;
  }
`
